import { useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCandidateData } from '@/components/candidates/CandidateDataProvider'
import { publicAsset } from '@/components/navigation/navigationAssets'
import {
  getCandidateDignityOptions,
} from '@/lib/data/candidates'
import { isNationalCandidateDignity } from '@/lib/data/ballotColors'
import {
  getTerritoryCantonSlugOptions,
  getTerritoryProvinceSlugOptions,
} from '@/lib/data/territoryCatalogs'
import { trackEvent } from '@/analytics'

const featuredCities = [
  {
    name: 'Quito',
    provinceId: 'pichincha',
    cantonId: 'quito',
    image: 'images/figma/home/quito.jpeg',
    tone: 'blue',
  },
  {
    name: 'Guayaquil',
    provinceId: 'guayas',
    cantonId: 'guayaquil',
    image: 'images/figma/home/guayaquil.jpg',
    tone: 'green',
  },
  {
    name: 'Cuenca',
    provinceId: 'azuay',
    cantonId: 'cuenca',
    image: 'images/figma/home/cuenca.jpeg',
    tone: 'black',
  },
] as const

type FilterFieldProps = {
  disabled?: boolean
  emptyLabel?: string
  icon: string
  label: string
  options: { id: string; label: string }[]
  value: string
  onChange: (value: string) => void
}

function CandidateFilterField({ disabled = false, emptyLabel, icon, label, onChange, options, value }: FilterFieldProps) {
  return (
    <label className="home-candidate-filter">
      <img src={publicAsset(icon)} alt="" />
      <span className="home-candidate-filter__control" data-mobile-label={label}>
        <small>{label}</small>
        <select disabled={disabled} value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">{emptyLabel ?? label}</option>
          {options.map((option) => (
            <option value={option.id} key={option.id}>{option.label}</option>
          ))}
        </select>
      </span>
    </label>
  )
}

export function HomeCandidateDiscovery() {
  const navigate = useNavigate()
  const { candidates } = useCandidateData()
  const [provinceId, setProvinceId] = useState('pichincha')
  const [cantonId, setCantonId] = useState('quito')
  const [dignityId, setDignityId] = useState('alcalde')
  const provinceOptions = useMemo(() => getTerritoryProvinceSlugOptions(), [])
  const cantonOptions = useMemo(
    () => getTerritoryCantonSlugOptions(provinceId),
    [provinceId],
  )
  const dignityOptions = useMemo(() => getCandidateDignityOptions(candidates), [candidates])
  const nationalDignity = isNationalCandidateDignity(dignityId)

  function updateProvince(nextProvinceId: string) {
    setProvinceId(nextProvinceId)
    const nextCantons = getTerritoryCantonSlugOptions(nextProvinceId)
    setCantonId((current) => nextCantons.some((option) => option.id === current) ? current : '')
  }

  function updateDignity(nextDignityId: string) {
    setDignityId(nextDignityId)
    if (!isNationalCandidateDignity(nextDignityId)) return
    setProvinceId('')
    setCantonId('')
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const query = new URLSearchParams()
    if (provinceId) query.set('provinceId', provinceId)
    if (cantonId) query.set('cantonId', cantonId)
    if (dignityId) query.set('dignityId', dignityId)
    trackEvent('candidate_filter', {
      event_category: 'candidate',
      filter_name: 'home_candidate_search',
      filter_value: query.toString() || 'all',
    })
    navigate(`/candidatos${query.size ? `?${query.toString()}` : ''}`)
  }

  return (
    <section className="home-candidate-discovery" aria-label="Conoce a tus candidatos">
      <header className="home-candidate-discovery__heading">
        <h2>Conoce a tu candidato</h2>
        <Link to="/candidatos">Ver todos →</Link>
      </header>

      <div className="home-city-banners">
        {featuredCities.map((city) => (
          <Link
            className={`home-city-banner home-city-banner--${city.tone}`}
            to={`/candidatos?provinceId=${city.provinceId}&cantonId=${city.cantonId}`}
            key={city.name}
          >
            <img src={publicAsset(city.image)} alt="" />
            <strong>{city.name}</strong>
            <span>Ver candidatos →</span>
          </Link>
        ))}
      </div>

      <form className="home-candidate-search" onSubmit={submit}>
        <strong>Busca a tus candidatos</strong>
        <div className="home-candidate-search__fields">
          <CandidateFilterField
            disabled={nationalDignity}
            emptyLabel={nationalDignity ? 'Todo Ecuador' : undefined}
            icon="icons/election/localizacion.svg"
            label="Provincia"
            options={provinceOptions}
            value={nationalDignity ? '' : provinceId}
            onChange={updateProvince}
          />
          <CandidateFilterField
            disabled={nationalDignity}
            emptyLabel={nationalDignity ? 'No aplica' : undefined}
            icon="icons/election/canton.svg"
            label="Cantón"
            options={cantonOptions}
            value={nationalDignity ? '' : cantonId}
            onChange={setCantonId}
          />
          <CandidateFilterField
            icon="icons/election/alcalde.svg"
            label="Dignidad"
            options={dignityOptions}
            value={dignityId}
            onChange={updateDignity}
          />
          <button type="submit">Ir</button>
        </div>
      </form>
    </section>
  )
}
